import { MapContainer, TileLayer, Marker, Popup, Polyline, ZoomControl, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import Button from "../../components/common/ui/Button";
import { SelectInput } from "../../components/common/ui/Inputs";
import { useDispatch, useSelector } from "react-redux";
import { dashboardAction, masterActions } from "../../_store";
import zipCodeCoordinates from "../../_helpers/zipCodeCoordinates.json"
import usCityCoordinates from "../../_helpers/usCityCoordinates.json"
import stateCoordinates from "../../_helpers/stateCoordinates.json"
import PutoInmateConnection from "./PutoInmateConnection";
import { showToastNotification } from "../../components/common/ui/NotificationModal/ToastNotication";
import { getStoredValue } from "../../_helpers/storagehelper";


/* =====================
   World Bounds
===================== */
const WORLD_BOUNDS = [
    [-85, -180],
    [85, 180],
];


function ConnectionStatus() {
    const mapRef = useRef(null);
    const [locations, setLocations] = useState([]);
    const [selectPUID, setSelectPUID] = useState(null);
    const [showLines, setShowLines] = useState(true);
    const [mapZoom, setMapZoom] = useState(6);
    const [isLoading, setIsLoading] = useState(false);

    // Data states
    const [AllInmateDetails, setAllInmateDetails] = useState([]);
    const [InmateDropdownDetails, setInmateDropdownDetails] = useState([]);
    const [AllPublicUserDetails, setAllPublicUserDetails] = useState([]);
    const [inmateConnectionDetails, setInmateConnectionDetails] = useState([]);
    const [inmateGroupedConnections, setInmateGroupedConnections] = useState({});
    const [PublicUserConnectionDetails, setPublicUserConnectionDetails] = useState([]);

    // View states
    const [viewType, setViewType] = useState('inmate');
    const [dropdownSelectedInmate, setDropdownSelectedInmate] = useState([]);
    const [selectedPublicUser, setSelectedPublicUser] = useState(null);
    const dispatch = useDispatch();
    const BREAKPOINT = 1200;
    const [activeInmateId, setActiveInmateId] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= BREAKPOINT);
    const commonFacilityCode = useSelector((state) => state.auth.commonFacilityCode) || getStoredValue("commonFacilityCode");
    const prevFacilityCodeRef = useRef(commonFacilityCode);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= BREAKPOINT) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);



    useEffect(() => {
        if (prevFacilityCodeRef.current !== commonFacilityCode) {
            setSelectPUID(null);
            setAllInmateDetails([]);
            setAllPublicUserDetails([]);
            setPublicUserConnectionDetails([]);
            setInmateConnectionDetails([]);
            setInmateGroupedConnections({});
            setDropdownSelectedInmate(null);
            setSelectedPublicUser(null);
            prevFacilityCodeRef.current = commonFacilityCode;
        }
    }, [commonFacilityCode]);

    // Load facility dropdown on initial
    useEffect(() => {
        dispatch(masterActions.LoadFacilityDropDown({ facilityID: 0, activeStatus: 1 }));
    }, [dispatch]);


    /* =====================
    API Handlers
 ===================== */

    const handlegetMapDetails = async (
        publicUserId,
        inmateId,
        publicInmateConnection,
        commonFacilityCode
    ) => {
        try {
            setIsLoading(true);

            const response = await dispatch(
                dashboardAction.GetDashboardPUMapDetails({
                    publicUserId,
                    inmateId,
                    publicInmateConnection,
                    facilityId: commonFacilityCode,
                })
            );
            const data = response?.payload || [];
            // Reset all states first (single source of truth)
            setAllPublicUserDetails([]);
            setAllInmateDetails([]);
            setPublicUserConnectionDetails([]);
            setInmateConnectionDetails([]);

            // CASE 1 — Public Users
            if (publicUserId === 1) {
                setAllPublicUserDetails(data);
            }

            // CASE 2 — Inmates
            else if (inmateId === 1) {
                setAllInmateDetails(data);
            }

            // CASE 3 — Public User Connections
            else if (publicUserId > 0 && publicInmateConnection === 1) {
                setPublicUserConnectionDetails(data);
            }

            // CASE 4 — Inmate Connections
            else if (inmateId > 0 && publicInmateConnection === 1) {
                if (data?.length == 0) {
                    showToastNotification(
                        "No connections found for the selected inmate.",
                        "warning"
                    );
                    return;
                }
                setInmateConnectionDetails(prev => ({ ...prev, ...data }));
                // setInmateConnectionDetails(data);
                setInmateGroupedConnections(prev => ({
                    ...prev,
                    [inmateId]: data
                }));
            }

            // CASE 5 — Facility Inmate List
            else if (commonFacilityCode > 0) {
                const dropdownItems = data.map(user => ({
                    label: `${user?.LastName ?? ""} ${user?.FirstName ?? ""}`.trim(),
                    value: user?.InmateID,
                    zip: user?.ZIP,
                    city: user?.City,
                    state: user?.StateName,
                }));

                setInmateDropdownDetails(dropdownItems);
                setAllInmateDetails(data);
            }
        } catch (error) {
            console.error("Map details error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    /* =====================
       Data Fetching
    ===================== */

    const mergedUsers = useMemo(() => {
        return [
            ...(PublicUserConnectionDetails || []),
            ...(AllInmateDetails || []),
            ...(AllPublicUserDetails || []),
            ...(InmateDropdownDetails || []),
            // ...(inmateConnectionDetails || [])
        ];
    }, [PublicUserConnectionDetails, AllPublicUserDetails, inmateConnectionDetails, InmateDropdownDetails]);

    useEffect(() => {
        let processedUsers = [...mergedUsers];
        setLocations(processedUsers);
    }, [mergedUsers, dropdownSelectedInmate, selectedPublicUser, viewType]);

    /* =====================
   Build Multi-Inmate Lines
===================== */

    const handlelatandlng = (user) => {
        if (user?.Latitude || user?.Longitude) return { lat: user?.Latitude, lng: user?.Longitude };

        const zip = user.ZIP || user?.zip;
        const city = user.City || user?.city;
        const state = user.StateName || user.state;

        let coords = null;

        // 1️⃣ ZIP (most accurate)
        if (zip && zipCodeCoordinates?.[zip]) {
            coords = zipCodeCoordinates[zip];
        }
        // 2️⃣ City + State
        else if (city && state && usCityCoordinates?.[`${city}, ${state}`]) {
            coords = usCityCoordinates[`${city}, ${state}`];
        }
        // 3️⃣ State as city key fallback
        else if (state && usCityCoordinates?.[state]) {
            coords = usCityCoordinates[state];
        }
        // 4️⃣ State fallback
        else if (state && stateCoordinates?.[state]) {
            coords = stateCoordinates[state];
        }

        return coords || { lat: 0, lng: 0 };
    };


    // const createLetterIcon = (
    //     letter,
    //     type = "inmate",
    //     isActive = false,
    //     badgeCount = 0
    // ) =>
    //     L.divIcon({
    //         className: "",
    //         html: `
    //     <div class="user-marker ${type} ${isActive ? "status-connected" : ""}">

    //         ${type === "inmate" && isActive ? `<div class="pulse-ring"></div>` : ""}

    //         <div class="user-marker-inner ${type === "inmate" && isActive ? "pulse" : ""}">
    //             ${letter}
    //         </div>

    //         ${type === "public" && badgeCount > 1
    //                 ? `<div class="badge-count blink-badge">${badgeCount}</div>`
    //                 : ""
    //             }
    //     </div>
    // `,
    //         iconSize: [26, 26],
    //         iconAnchor: [13, 26],
    //     });

    const createLetterIcon = (
        letter,
        type = "inmate",
        isActive = false,
        badgeCount = 0
    ) =>
        L.divIcon({
            className: "",
            html: `
        <div style="width:48px;height:48px;display:flex;align-items:center;justify-content:center;">
        <div class="user-marker ${type} ${isActive ? "status-connected" : ""}">
            
            ${type === "inmate" && isActive ? `<div class="pulse-ring"></div>` : ""}

            <div class="user-marker-inner ${type === "inmate" && isActive ? "pulse" : ""}">
                ${letter}
            </div>

            ${type === "public" && badgeCount > 1
                    ? `<div class="badge-count blink-badge">${badgeCount}</div>`
                    : ""
                }
        </div>
        </div>
    `,
            iconSize: [48, 48],
            iconAnchor: [24, 24],
            popupAnchor: [0, -24],
        });

    const handleMarkerHoverIn = useCallback((e) => {
        const marker = e.target;
        if (marker._popupCloseTimeout) {
            clearTimeout(marker._popupCloseTimeout);
            marker._popupCloseTimeout = null;
        }
        marker.openPopup();
    }, []);

    const handleMarkerHoverOut = useCallback((e) => {
        const marker = e.target;
        marker._popupCloseTimeout = setTimeout(() => {
            marker.closePopup();
        }, 120);
    }, []);



    const connectionLines = useMemo(() => {
        if (!inmateGroupedConnections || !locations?.length) return [];

        const lines = [];

        Object.entries(inmateGroupedConnections).forEach(
            ([inmateId, publicUsers]) => {

                const inmateMarker = locations.find(
                    l => l.value === Number(inmateId)
                );

                if (!inmateMarker) return;

                const inmateCoords = handlelatandlng(inmateMarker);

                publicUsers?.forEach(user => {

                    const publicCoords = handlelatandlng(user);
                    if (!publicCoords) return;

                    lines.push({
                        inmateId: Number(inmateId),
                        inmateName: inmateMarker.label,   // important
                        publicUserId: user.PublicUserID,
                        publicUserName: user.UserName, // important
                        from: [inmateCoords.lat, inmateCoords.lng],
                        to: [publicCoords.lat, publicCoords.lng],
                        InmateConnectionCount: publicUsers?.length || 0,
                    });
                });
            }
        );

        return lines;
    }, [inmateGroupedConnections, locations]);

    const offsetMarkers = (items, offset = 0.15) => {
        const groups = new Map();

        // Group by position
        items.forEach(item => {
            const key = item.position.join(",");
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        });

        const result = [];

        groups.forEach(group => {
            if (group.length === 1) {
                result.push(group[0]);
                return;
            }

            const center = group[0].position;
            const angleStep = (2 * Math.PI) / group.length;

            group.forEach((item, index) => {
                const angle = index * angleStep;

                const latOffset = offset * Math.cos(angle);
                const lngOffset = offset * Math.sin(angle);

                result.push({
                    ...item,
                    position: [
                        center[0] + latOffset,
                        center[1] + lngOffset
                    ]
                });
            });
        });

        return result;
    };

    const offsetLookup = useMemo(() => {
        const combined = [];

        connectionLines.forEach(line => {
            combined.push({
                type: "inmate",
                id: line.inmateId,
                name: line.inmateName,
                position: line.from
            });

            combined.push({
                type: "public",
                id: line.publicUserId,
                name: line.publicUserName,
                position: line.to
            });
        });

        const offsetAll = offsetMarkers(combined, 0.25);

        const lookup = new Map();
        offsetAll.forEach(m => {
            lookup.set(`${m.type}-${m.id}`, m.position);
        });

        return lookup;
    }, [connectionLines]);


    const uniqueInmates = useMemo(() => {
        const map = new Map();
        connectionLines.forEach(line => {
            if (!map.has(line.inmateId)) {
                map.set(line.inmateId, {
                    inmateId: line.inmateId,
                    inmateName: line.inmateName,
                    position: offsetLookup.get(`inmate-${line.inmateId}`),
                    InmateConnectionCount: line.InmateConnectionCount
                });
            }
        });

        if (map.size > 0) {
            const firstValue = map.values().next().value;
            const fstselinmateid = firstValue.inmateId;
            setActiveInmateId(fstselinmateid);
        }
        return Array.from(map.values());
    }, [connectionLines, offsetLookup]);



    const activeConnectionPU = inmateGroupedConnections?.[activeInmateId] || [];

    const uniquePublicUsers = useMemo(() => {
        const map = new Map();

        connectionLines.forEach(line => {
            if (!map.has(line.publicUserId)) {
                map.set(line.publicUserId, {
                    publicUserId: line.publicUserId,
                    publicUserName: line.publicUserName,
                    // position: publicUserPositionMap.get(line.publicUserId)
                    position: offsetLookup.get(`public-${line.publicUserId}`)

                });
            }
        });

        return Array.from(map.values());
    }, [connectionLines, offsetLookup]);


    /* =====================
       Event Handlers
    ===================== */

    const onInmateChange = (selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setDropdownSelectedInmate([]);
            setInmateGroupedConnections({});
            return;
        }
        setInmateGroupedConnections(prev => {
            const updated = Object.keys(prev)
                .filter(key => selectedOptions.includes(Number(key)))
                .reduce((obj, key) => {
                    obj[key] = prev[key];
                    return obj;
                }, {});
            return updated;
        });

        // Convert to the format SelectInput expects
        const formatted = selectedOptions.map(opt => ({
            InmateMenuID: opt.value
        }));

        setDropdownSelectedInmate(formatted);
        //setAllInmateDetails([]);
        //setPublicUserConnectionDetails([]);
        //setDropdownSelectedInmate(selectedOption);
        setViewType('inmate');
        setSelectedPublicUser(null);
        // handlegetMapDetails(0, selectedOption?.value, 1, 0);

        selectedOptions.forEach(option => {
            handlegetMapDetails(0, option.value, 1, 0);
        });

    }

    const handlePublicUserSelect = async (user) => {
        if (user?.publicUserId || user?.PublicUserID) {
            setSelectPUID(user);
        }
    }


    const handleRemoveUser = async (user) => {
        if (user.inmateId) {
            setInmateGroupedConnections(prev => {
                const { [user.inmateId]: removed, ...rest } = prev;
                return rest;
            });
            if (Object.keys(inmateGroupedConnections).length == 1) {
                setDropdownSelectedInmate(null);
            }
            else if (user.inmateId == dropdownSelectedInmate?.value) {
                const newFirstInmateId = Object.keys(inmateGroupedConnections).find(id => id != user.inmateId);
                const selectInmate = InmateDropdownDetails?.find(inmate => inmate.value == newFirstInmateId);
                setDropdownSelectedInmate(selectInmate);
            }
            setDropdownSelectedInmate(dropdownSelectedInmate?.filter((x) => x?.InmateMenuID !== user.inmateId));

        }
    }

    useEffect(() => {
        if (commonFacilityCode != 0) {
            handlegetMapDetails(0, 0, 0, commonFacilityCode);
        }
    }, [commonFacilityCode]);

    /* =====================
       UI Helpers
    ===================== */

    const shouldShowSidebar = () => {
        if (commonFacilityCode === 0) return false;
        if (viewType === 'inmate' && dropdownSelectedInmate) return true;
        if (viewType === 'public' && selectedPublicUser) return true;
        return false;
    };


    const renderSidebarContent = () => {
        if (viewType === 'inmate' && dropdownSelectedInmate) {
            return (
                <>
                    <div className="sidebar-header">
                        <div className="header-left">
                            <span title="HIDE MENU"
                                className="toggle-icon"
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            >
                                ☰
                            </span>

                            <h4 ><i className="fa fa-link me-2"></i>Inmate</h4>
                        </div>
                    </div>

                    <div className={`connections-list ${isSidebarOpen ? "open" : "closed"}`}>

                        <div className="">
                            <div className="public-users-list">
                                {uniqueInmates.map(user => (
                                    <div
                                        key={user.id || user.PublicUserID}
                                        className="connection-item inmate-item connected"
                                    >
                                        {/* 🔴 Close Icon */}
                                        <div
                                            className="close-icon"
                                            onClick={(e) => {
                                                e.stopPropagation(); // prevent card click
                                                handleRemoveUser(user); // your remove function
                                            }}
                                        >
                                            <i className="fa fa-times"></i>
                                        </div>

                                        <div
                                            className="user-info-container"
                                            onClick={() => {
                                                if (user.inmateId === activeInmateId) {
                                                    setActiveInmateId(null); // toggle off if same inmate clicked
                                                } else {
                                                    setActiveInmateId(user.inmateId);
                                                }
                                            }}
                                        //  onClick={() => handlePublicUserSelect(user)}
                                        >
                                            <div
                                                className={`user-icon-sidebar inmate ${user.inmateId === activeInmateId ? "active-inmate" : ""
                                                    }`}
                                            >
                                                {(user.inmateName || user?.FirstName)?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>

                                            <div className="user-details-sidebar">
                                                <div className="user-name-sidebar">
                                                    {user.inmateName || user?.FirstName}
                                                    <div style={{ fontSize: '1rem', marginTop: '4px' }}>
                                                        <h6> Inmate ID:  {user.inmateId || user?.InmateID}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="connected-inmates-wrapper">
                                            <div className="count-badge">
                                                <i className="fa fa-user" aria-hidden="true"></i>
                                                <span>{user.InmateConnectionCount || 0}</span>
                                            </div>
                                        </div>
                                    </div>

                                ))}
                            </div>
                        </div>
                        {activeInmateId && activeConnectionPU?.length > 0 && (
                            <div className="public-users-section">
                                <div className="sidebar-header">
                                    <h4>Connected Public Users</h4>
                                    <div className="menu-badge">
                                        {activeConnectionPU?.length || 0}
                                    </div>
                                </div>
                                <div className="public-users-list">
                                    {activeConnectionPU?.map((publicUser) => (
                                        <div
                                            key={`public-${publicUser.PublicUserID}-${activeInmateId}`}
                                            position={publicUser.position}
                                            className="connection-item public-item connected"
                                            onClick={() => {
                                                const userWithPosition = uniquePublicUsers.find(
                                                    u => u.publicUserId === publicUser.PublicUserID
                                                );

                                                handlePublicUserSelect({
                                                    PublicUserID: publicUser.PublicUserID,
                                                    UserName: publicUser.UserName,
                                                    position: userWithPosition?.position || publicUser.position || [0, 0],
                                                    lat: userWithPosition?.position?.[0] || publicUser.position?.[0] || 0,
                                                    lng: userWithPosition?.position?.[1] || publicUser.position?.[1] || 0,
                                                });
                                            }}
                                        >
                                            <div className="user-info-container">
                                                <div className="user-icon-sidebar public">
                                                    {publicUser.UserName?.charAt(0).toUpperCase() || 'U'}
                                                </div>

                                                <div className="user-details-sidebar">
                                                    <div className="user-name-sidebar">
                                                        {publicUser.UserName}
                                                    </div>
                                                    <div className="user-email-sidebar">
                                                        {publicUser.Email || ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            );
        } else if (viewType === 'public' && selectedPublicUser) {
            // Public user view - show selected public user and connected inmates
            const connectedInmates = locations.filter(p => p.InmateID > 0);

            return (
                <>
                    <div className="sidebar-header">
                        <h4>Public User</h4>
                    </div>
                    <div className="connections-list">
                        <div className="connection-item public-user">
                            <div className="user-info-container">
                                <div className="user-icon-sidebar public">
                                    {selectedPublicUser.UserName?.charAt(0).toUpperCase() || selectedPublicUser.FirstName?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div className="user-details-sidebar">
                                    <div className="user-name-sidebar">{selectedPublicUser.UserName || selectedPublicUser.FirstName}</div>
                                </div>
                            </div>
                        </div>

                        <div className="public-users-section">
                            <div className="sidebar-header">
                                <h4>Connected Inmates</h4>
                                <div className="menu-badge">{connectedInmates.length}</div>
                            </div>
                            <div className="public-users-list">
                                {connectedInmates.map(inmate => (
                                    <div
                                        key={inmate.InmateID}
                                        className="connection-item inmate-item connected"
                                    >
                                        <div className="user-info-container">
                                            <div className="user-icon-sidebar inmate">
                                                {inmate.UserName?.charAt(0).toUpperCase() || inmate.FirstName?.charAt(0).toUpperCase() || 'U'}
                                            </div>
                                            <div className="user-details-sidebar">
                                                <div className="user-name-sidebar">{inmate.UserName || inmate.FirstName}</div>
                                            </div>
                                        </div>
                                        <div className="connected-inmates-wrapper">
                                            <div className="count-label">Connected</div>
                                            <div className="count-badge">
                                                <i className="fa fa-user" aria-hidden="true"></i>
                                                <span>{inmate.InmateConnectedPublicUserCount || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            );
        }
        return null;
    };

    /* =====================
       Render Component
    ===================== */
    if (commonFacilityCode == 0) {
        return (
            <h4 className="facility-card">
                Select a Facility Code from the list
            </h4>
        )
    }
    else if (selectPUID) {
        return <PutoInmateConnection selectPUID={selectPUID} setSelectPUID={setSelectPUID} setIsSidebarOpen={setIsSidebarOpen} isSidebarOpen={isSidebarOpen} />;
    }
    else {
        return (
            <div className="app-container">
                <div className="map-section">
                    <div className="map-header">
                        <h2 className="form_title_uppercase text-color">
                            Connection Insights
                        </h2>

                        <div className="map-controls">
                            {commonFacilityCode !== 0 && (
                                <div className="header-common-style d-flex flex-wrap">
                                    <label className="header-common-label" htmlFor="">
                                        Inmate
                                    </label>
                                    <div style={{ width: "335px" }}>
                                        <SelectInput
                                            name="value"
                                            label=""
                                            options={InmateDropdownDetails}
                                            value={dropdownSelectedInmate}
                                            isMulti={true}
                                            handleChange={onInmateChange}
                                            closeMenuOnSelect={false}
                                            classNamePrefix={"header-facility-Dashboard"}
                                            isClearable={false}
                                            isDashboardPage={true}
                                        />
                                    </div>
                                </div>
                            )}
                            {/* <Button
                                className="me-2"
                                label={showLines ? 'Hide Lines' : 'Show Lines'}
                                type="button"
                                onClick={() => setShowLines(!showLines)}
                                variant="danger"
                                status="btn-label"
                                disabled={false}
                            /> */}
                        </div>
                    </div>

                    <div className="map-container-wrapper">
                        {uniqueInmates?.length > 0 && shouldShowSidebar() && (
                            <div className={`map-sidebar ${isSidebarOpen ? "open" : "closed"}`}>
                                {renderSidebarContent()}
                            </div>
                        )}

                        {!isSidebarOpen && (
                            <div title="OPEN MENU" className="floating-menu" onClick={() => setIsSidebarOpen(true)}>
                                ☰
                            </div>
                        )}
                        {uniqueInmates?.length > 0 ?
                            <div className="map-container">
                                {isLoading ? (
                                    <div className="map-loading">
                                        <div className="spinner"></div>
                                        <p>Loading map ...</p>
                                    </div>
                                ) : (
                                    <MapContainer
                                        center={[28.5, -81.3]}
                                        zoom={6}
                                        minZoom={2}
                                        maxZoom={18}
                                        maxBounds={WORLD_BOUNDS}
                                        maxBoundsViscosity={1.0}
                                        zoomControl={false}
                                        scrollWheelZoom={true}
                                        doubleClickZoom={true}
                                        touchZoom={true}
                                        dragging={true}
                                        whenCreated={(map) => {
                                            mapRef.current = map;
                                            map.on('zoom', () => {
                                                setMapZoom(map.getZoom());
                                            });
                                        }}
                                    >
                                        <LayersControl position="topright">

                                            <LayersControl.BaseLayer checked name="Street View">
                                                <TileLayer
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                    attribution="© OpenStreetMap contributors"
                                                />
                                            </LayersControl.BaseLayer>

                                            <LayersControl.BaseLayer name="Satellite View">
                                                <TileLayer
                                                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                                    attribution="Tiles © Esri"
                                                />
                                            </LayersControl.BaseLayer>

                                        </LayersControl>
                                        <ZoomControl position="bottomright" />


                                        {/* Render Inmate Markers Once */}
                                        {uniqueInmates.map(inmate => {
                                            const inmateLetter = inmate.inmateName?.charAt(0)?.toUpperCase() || "I";

                                            return (
                                                <Marker
                                                    key={`inmate-${inmate.inmateId}-${activeInmateId}`}
                                                    position={inmate.position}
                                                    icon={createLetterIcon(
                                                        inmateLetter,
                                                        "inmate",
                                                        inmate.inmateId === activeInmateId
                                                    )}
                                                    eventHandlers={{
                                                        click: () => {
                                                            if (inmate.inmateId === activeInmateId) {
                                                                setActiveInmateId(null); // toggle off if same inmate clicked
                                                            } else {
                                                                setActiveInmateId(inmate.inmateId);
                                                            }
                                                        },
                                                        mouseover: handleMarkerHoverIn,
                                                        mouseout: handleMarkerHoverOut
                                                    }}
                                                >
                                                    <Popup>
                                                        <div className="">
                                                            {/* <div className="map-popup"> */}
                                                            <h6>{inmate.inmateName}</h6>
                                                        </div>

                                                    </Popup>
                                                </Marker>
                                            );
                                        })}

                                        {/* Render Public User Markers Once */}
                                        {uniquePublicUsers.map(user => {
                                            const publicLetter = user.publicUserName?.charAt(0)?.toUpperCase() || "P";

                                            const isConnectedToActiveInmate = activeInmateId &&
                                                inmateGroupedConnections[activeInmateId]?.some(
                                                    u => u.PublicUserID === user.publicUserId
                                                );

                                            const connectedInmateCount = Object.values(inmateGroupedConnections || {})
                                                .flat()
                                                .filter(u => u.PublicUserID === user.publicUserId)
                                                .length;

                                            return (
                                                <Marker
                                                    key={`public-${user.publicUserId}-${activeInmateId}`}
                                                    position={user.position}
                                                    setSelectPUID={user.position}
                                                    icon={createLetterIcon(
                                                        publicLetter,
                                                        "public",
                                                        isConnectedToActiveInmate, connectedInmateCount
                                                    )}

                                                    eventHandlers={{
                                                        click: () => handlePublicUserSelect(user),
                                                        mouseover: (e) => {
                                                            e.target.openPopup();
                                                        },
                                                        mouseout: (e) => {
                                                            e.target.closePopup();
                                                        }
                                                    }}
                                                >
                                                    <Popup>
                                                        <div >
                                                            <h6>{user.publicUserName}</h6>
                                                        </div>
                                                    </Popup>
                                                </Marker>
                                            );
                                        })}

                                        {/* Render All Connection Lines */}
                                        {connectionLines.map(line => {
                                            const from = offsetLookup.get(`inmate-${line.inmateId}`);
                                            const to = offsetLookup.get(`public-${line.publicUserId}`);

                                            if (!from || !to) return null;
                                            const isActive = line.inmateId === activeInmateId;
                                            return (
                                                <Polyline
                                                    key={`${line.inmateId}-${line.publicUserId}`}
                                                    positions={[from, to]}
                                                    pathOptions={{
                                                        color: isActive ? "#000000" : "#4CAF50",
                                                        weight: 2,
                                                        dashArray: "6, 6"
                                                    }}
                                                />
                                            );
                                            // return (
                                            //     <Polyline
                                            //         key={`${line.inmateId}-${line.publicUserId}`}
                                            //         positions={[from, to]}
                                            //         pathOptions={{
                                            //             color: "#4CAF50",
                                            //             weight: 2,
                                            //             dashArray: "6, 6"
                                            //         }}
                                            //     />
                                            // );
                                        })}


                                    </MapContainer>
                                )}
                            </div>
                            :
                            <div className=" w-100 ">
                                <h4 className="facility-card">
                                    Select a Inmate from the list
                                </h4>
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }
}
export default React.memo(ConnectionStatus);